import React, {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  MouseEventHandler,
  useMemo,
} from 'react';
import classNames from 'classnames';

import { Spinner } from '@components/Spinner';
import { getAttributes } from '@utils/getAttributes';

type Props = {
  label: string;
  icon?: React.ReactElement;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  variant?: 'primary' | 'outlined';
  classNameContainer?: string;
  full?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  labelContainerClassName?: string;
  tourId?: string;
} & DetailedHTMLProps<
  ButtonHTMLAttributes<HTMLButtonElement>,
  HTMLButtonElement
>;

export function Button({
  label,
  icon,
  onClick,
  variant = 'primary',
  classNameContainer,
  full,
  isLoading,
  disabled,
  labelContainerClassName,
  tourId,
  ...rest
}: Props) {
  const buttonClasses = useMemo(
    () =>
      classNames(
        'flex w-fit cursor-pointer flex-row items-center justify-center rounded-[0.625rem] p-4 transition active:transition-none',
        {
          'bg-button-primary-bg-default text-button-primary-text hover:text-button-primary-text hover:bg-button-primary-bg-hover active:bg-button-primary-bg-active active:text-button-primary-text':
            variant === 'primary',
        },
        {
          '!pointer-events-none border-[1px] !border-none !bg-button-primary-bg-disabled !text-button-primary-disabled-text cursor-not-allowed':
            variant === 'primary' && disabled,
        },
        {
          'border-[1px] border-button-outlined-border hover:border-button-outlined-border-hover active:border-button-outlined-border-active bg-transparent text-button-outlined-text hover:text-button-outlined-text-hover hover:bg-button-outlined-bg active:bg-button-outlined-bg active:text-button-outlined-text-active':
            variant === 'outlined',
        },
        {
          '!pointer-events-none !border-button-outlined-border-disabled !bg-transparent !text-button-outlined-disabled-text cursor-not-allowed':
            variant === 'outlined' && disabled,
        },
        { 'w-full': full },
        { 'cursor-wait': isLoading },
        classNameContainer
      ),
    [classNameContainer, disabled, full, isLoading, variant]
  );

  return (
    <button
      className={buttonClasses}
      onClick={!isLoading ? onClick : () => null}
      disabled={disabled}
      {...getAttributes({ tourId })}
      {...rest}
    >
      {isLoading && <Spinner className='mr-2 !h-5 !w-5' />}
      {icon && <div className='mr-[0.625rem]'>{icon}</div>}
      <div
        className={classNames(
          'text-base font-semibold leading-[1.5rem] md:text-[18px] md:leading-6',
          labelContainerClassName
        )}
      >
        {label}
      </div>
    </button>
  );
}
